import { Box, Divider, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  black,
  customError,
  customSuccess,
  lightBackground1,
  lightBlack,
  lightGrey,
  warningYellow,
} from '../../mainTheme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  CustomerCareHubRow,
  CustomerCarePatientRow,
} from '../../store/hubOwner/types';
import { DashboardTabs } from '../Dashboard/components/DashboardTabs';
import CustomerCareDTable from '../Shared/CustomerCareDTable';
import DashboardTableLegend from '../Dashboard/detailPanel/DashboardTableLegend';
import { getFacilityRoleById, parseDateToLocal } from '../../shared/utils';
import FacilityStatusIcon from '../ModernFacility/components/FacilityStatusIcon';
import { fetchHubOwners } from '../../store/hubOwner/actionCreators';
import NewModal from '../Shared/NewModal';
import AddPatientController from '../Dashboard/AddNewController/components/AddPatientController';
import { UserRoles } from '../../shared/constants';

const PatientOverview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { hubOwners, isLoading } = useSelector(
    (state: RootState) => state.hubOwner,
  );

  const { dashboardUserInfo } = useSelector(
    (state: RootState) => state.dashboardUser,
  );

  const currentRole = dashboardUserInfo?.facility_role_id
    ? getFacilityRoleById(dashboardUserInfo.facility_role_id)
    : null;

  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const [isAddPatientOpen, setIsAddPatientOpen] = useState(false);
  const [patientStage, setPatientStage] = useState('initial');

  const isFacilityAdmin = currentRole === UserRoles.FACILITY_ADMIN;

  useEffect(() => {
    if (!hubOwners?.length) {
      dispatch(fetchHubOwners());
    }
  }, [dispatch, hubOwners?.length]);

  const columns = [
    {
      field: 'patient_name',
      headerName: 'Patient',
      flex: 1,
    },
    {
      field: 'med_status_level',
      headerName: 'Status',
      flex: 0.5,
      sortable: true,
      renderCell: (params: any) => {
        return (
          <FacilityStatusIcon
            status={params.value as 'ok' | 'warning' | 'critical'}
          />
        );
      },
    },
    {
      field: 'next_med_event',
      headerName: 'Next med event',
      flex: 1,
      renderCell: (params: any) =>
        params.value
          ? parseDateToLocal(params.value, 'EEE h:mm a MMM dd yyyy')
          : 'N/A',
    },
    ...(selectedTab !== 2
      ? [
          { field: 'parent_facility_name', headerName: 'Facility', flex: 1 },
          { field: 'facility_location', headerName: 'Location', flex: 1 },
        ]
      : [{ field: 'address', headerName: 'Address', flex: 1.4 }]),
  ];

  const rows: CustomerCarePatientRow[] = useMemo(() => {
    let filteredPatients = hubOwners || [];

    if (hubOwners) {
      if (selectedTab === 1) {
        filteredPatients = filteredPatients?.filter(
          (patient) => patient.facility_id != null,
        );
      } else if (selectedTab === 2) {
        filteredPatients = filteredPatients?.filter(
          (patient) => patient.facility_id == null,
        );
      }
    }
    return (
      filteredPatients?.map((patient) => {
        return {
          id: patient.customer_id,
          patient_name:
            `${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
            'N/A',
          med_status_level: patient.med_status_level || 'N/A',
          next_med_event: patient.next_med_event || null,
          parent_facility_name: patient.parent_facility_name || 'N/A',
          facility_location: patient.facility_name || 'N/A',
          address:
            selectedTab === 2
              ? patient.city && patient.state
                ? `${patient.city}, ${patient.state}`
                : 'N/A'
              : undefined,
        };
      }) ?? []
    );
  }, [hubOwners, selectedTab]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleRowClick = (row: CustomerCareHubRow) => {
    navigate(`/v2/patients/${row.id}`);
  };

  const handlePatientStageChange = (stage: string) => {
    setPatientStage(stage);
  };

  const filteredData = rows?.filter((row) => {
    const matchesSearchTerm =
      searchTerm === '' ||
      row.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.med_status_level.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.next_med_event?.toLowerCase() || '').includes(
        searchTerm.toLowerCase(),
      ) ||
      row.parent_facility_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      row.facility_location.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearchTerm;
  });

  const tableLegendData = [
    {
      name: '0',
      color: customSuccess,
    },
    {
      name: '1',
      color: warningYellow,
    },
    {
      name: '2 or more',
      color: customError,
    },
  ];

  const totalCount = rows?.length || 0;

  return (
    <Box display="flex" flexDirection="column" bgcolor={lightBackground1}>
      <DashboardTabs
        selectedTab={selectedTab}
        onTabChange={(tab) => setSelectedTab(tab)}
        buttonConfig={{
          show: true,
          label: 'Add new patient',
          onClick: () => {
            setPatientStage('initial');
            setIsAddPatientOpen(true);
          },
        }}
        isFacilityAdmin={isFacilityAdmin}
      />

      <Box
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        p={2}
      >
        <Typography sx={{ color: lightBlack }}>
          <Box component="span" sx={{ color: black }}>
            {totalCount}
          </Box>
          {' Patients'}
        </Typography>
        <TextField
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <Box display="flex" alignItems="center" mr={1}>
                <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
              </Box>
            ),
            sx: {
              borderRadius: '10px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D4D4D9',
              },
              color: lightBlack,
              '& input::placeholder': {
                color: '#7F7F7F',
                opacity: 1,
              },
            },
          }}
          sx={{
            minWidth: '566px',
            '& .MuiInputLabel-root': {
              color: lightBlack,
            },
          }}
        />
      </Box>
      <Box px={2}>
        <Divider />
      </Box>

      <Box px={2} mt={2}>
        <Box display="flex" gap={4} alignItems="center">
          <DashboardTableLegend tableLegendData={tableLegendData} />
          <Typography
            sx={{
              color: lightGrey,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
            }}
          >
            missed med events in the last 24 hrs
          </Typography>
        </Box>

        <CustomerCareDTable
          rows={filteredData}
          columns={columns}
          loading={isLoading}
          pageSize={10}
          defaultSortField="med_status_level"
          defaultSortDirection="desc"
          hideViewAllButton
          minHeight="200px"
          onRowClick={handleRowClick}
        />
      </Box>

      {isAddPatientOpen && (
        <NewModal
          showCloseX
          title="Add new"
          onClose={() => setIsAddPatientOpen(false)}
          maxWidth={patientStage === 'initial' ? 'xs' : 'sm'}
        >
          <AddPatientController
            onClose={() => setIsAddPatientOpen(false)}
            onBack={() => setIsAddPatientOpen(false)}
            onStageChange={handlePatientStageChange}
            onPatientAdded={() => dispatch(fetchHubOwners())} // Used to refresh the patient list
            isFacilityAdmin={isFacilityAdmin}
          />
        </NewModal>
      )}
    </Box>
  );
};

export default PatientOverview;
